import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import {useParams, useSearchParams} from 'react-router-dom'
import {useTranslation} from 'react-i18next'
import {type PatientAppointmentItem, SlotStatusType} from '@common/models'
import {formatDateLegend} from '@common/utils/dates'
import {yymmToYearMonth, currentYyMm, getCalendarMatrix} from '@common/utils'

import {
  PageWrapper,
  SelectedGlobal,
  OrangeButton,
} from './AppointmentManagementPage.styled'
import {BookingsHeader} from './BookingsHeader'
import {FooterInfo} from './FooterInfo'
import {PoweredBy} from './PoweredBy'
import {AppointmentsList} from './AppointmentsList'
import {ReschedulePanel} from './ReschedulePanel'
import {CancelPanel} from './CancelPanel'
import {SlotHistoryModal} from './SlotHistoryModal'

import {
  useGetBookingsPortalPatientAppointments,
  useGetBookingsPortalAvailabilities,
  useBookingsPortalCancelAppointment,
  useBookingsPortalRescheduleAppointment,
} from '~/api/bookingsPortal'
import {Modal} from '~/components/general/modal/Modal'
import {Container} from '~/components/general/container/Container'
import {Typography} from '~/components/general/typography/Typography'
import {ProcessingOverlay} from '~/components/general/processingOverlay/ProcessingOverlay'
import {
  NotificationContext,
  NotificationType,
} from '~/components/notifications/NotificationContext'

type AvailabilityDay = {date: string; slots: string[]}

export const AppointmentManagementPage = () => {
  const {t} = useTranslation()
  const {patientId = ''} = useParams()
  const [searchParams] = useSearchParams()
  const rescheduleSlotId = searchParams.get('reschedule')
  const cancelSlotId = searchParams.get('cancel')
  const isMobileContext = searchParams.get('source') === 'mobile'

  const {data, isError, isLoading} = useGetBookingsPortalPatientAppointments(
    patientId,
    {
      staleTime: 0,
      gcTime: 0,
      refetchOnMount: 'always',
      refetchOnWindowFocus: 'always',
    },
  )

  const [openSlotId, setOpenSlotId] = useState<string | null>(null)
  const [openRescheduleId, setOpenRescheduleId] = useState<string | null>(null)
  const [openCancelId, setOpenCancelId] = useState<string | null>(null)

  // Reschedule UI state
  // API month spec: starts at 'FIRST' each time reschedule opens; user navigation sets YYMM
  const [apiMonthSpec, setApiMonthSpec] = useState<string>('FIRST')
  // Calendar visible month (YYMM) for UI only; initial current, then synced from API once
  const [calendarYyMm, setCalendarYyMm] = useState<string>(currentYyMm())

  const effectiveYyMm = useMemo(() => calendarYyMm, [calendarYyMm])
  const [selectedDate, setSelectedDate] = useState<string | null>(null)
  const [selectedTime, setSelectedTime] = useState<string | null>(null)
  const [rescheduleComment, setRescheduleComment] = useState<string>('')
  const [cancelComment, setCancelComment] = useState<string>('')

  const {addNotification} = useContext(NotificationContext)

  const cancelMutation = useBookingsPortalCancelAppointment(patientId)
  const rescheduleMutation = useBookingsPortalRescheduleAppointment(patientId)

  // Compute partnerServiceId from the selected item
  const rescheduleServiceId = useMemo(() => {
    if (!openRescheduleId) return null
    const item = data?.items.find(
      (i: PatientAppointmentItem) => i.slotId === openRescheduleId,
    )
    return item?.partnerServiceId ?? null
  }, [openRescheduleId, data?.items])

  // Load availabilities for reschedule
  const {
    data: avail,
    isLoading: isLoadingAvailabilities,
    isFetching: isFetchingAvailabilities,
  } = useGetBookingsPortalAvailabilities(
    rescheduleServiceId || '',
    apiMonthSpec,
    {
      enabled: !!rescheduleServiceId,
      staleTime: 0,
      gcTime: 0,
      refetchOnMount: 'always',
      refetchOnWindowFocus: 'always',
    },
  )
  const days = useMemo<AvailabilityDay[]>(
    () => (avail?.days as AvailabilityDay[]) ?? [],
    [avail?.days],
  )

  // Set visible calendar month based on API response ONCE per open
  const didSetCalendarFromApiRef = useRef(false)
  const didApplyUrlParamsRef = useRef(false)

  // Auto-open reschedule or cancel panel when valid slotId is provided in URL
  useEffect(() => {
    if (isLoading || !data?.items?.length || didApplyUrlParamsRef.current)
      return
    const items = data.items as PatientAppointmentItem[]
    const isScheduled = (i: PatientAppointmentItem) =>
      i.status === SlotStatusType.SCHEDULED

    if (rescheduleSlotId) {
      const item = items.find(
        (i) => i.slotId === rescheduleSlotId && isScheduled(i),
      )
      if (item) {
        setOpenRescheduleId(rescheduleSlotId)
        didApplyUrlParamsRef.current = true
        return
      }
    }
    if (cancelSlotId) {
      const item = items.find(
        (i) => i.slotId === cancelSlotId && isScheduled(i),
      )
      if (item) {
        setOpenCancelId(cancelSlotId)
        didApplyUrlParamsRef.current = true
      }
    }
  }, [data?.items, isLoading, rescheduleSlotId, cancelSlotId])

  // Ensure each time we open reschedule, we start with FIRST and reset calendar sync
  useEffect(() => {
    if (!openRescheduleId) return
    setApiMonthSpec('FIRST')
    setCalendarYyMm(currentYyMm())
    didSetCalendarFromApiRef.current = false
  }, [openRescheduleId])

  useEffect(() => {
    if (!avail) return
    if (didSetCalendarFromApiRef.current) return
    const yymm = avail?.yearMonth
    if (yymm) setCalendarYyMm(yymm)
    didSetCalendarFromApiRef.current = true
  }, [avail])

  // Calendar derived data
  const cells = useMemo(() => getCalendarMatrix(effectiveYyMm), [effectiveYyMm])
  // weekdayShorts handled inside ReschedulePanel

  const selectedDateLegend = useMemo(
    () => formatDateLegend(selectedDate || undefined),
    [selectedDate],
  )

  // Build slots map from API days
  const slotsByDate = useMemo(() => {
    const map = new Map<string, string[]>()
    days.forEach((d: AvailabilityDay) => map.set(d.date, d.slots))
    return map
  }, [days])

  // Initialize selected date from first available day in current month
  useEffect(() => {
    if (!openRescheduleId) return
    const first = days.find((d: AvailabilityDay) => (d.slots?.length || 0) > 0)
    if (first) {
      setSelectedDate(first.date)
    } else if (avail) {
      const {year, month} = yymmToYearMonth(effectiveYyMm)
      const firstDayOfMonth = `${year}/${String(month).padStart(2, '0')}/01`
      setSelectedDate(firstDayOfMonth)
    }
    setSelectedTime(null)
  }, [openRescheduleId, days, avail, effectiveYyMm])

  // month navigation handled inside ReschedulePanel via onMonthChange

  // Combine date (yyyy/mm/dd) and time (HH:mm) into a Date for comparisons/sorting
  const toDateTime = useCallback((dateStr: string, timeStr: string) => {
    const [yyyy, mm, dd] = dateStr.split('/').map((v) => Number(v))
    const [hh, min] = timeStr.split(':').map((v) => Number(v))
    // Months are 0-based in JS Date
    return new Date(yyyy, (mm || 1) - 1, dd || 1, hh || 0, min || 0)
  }, [])

  const {upcoming, past} = useMemo(() => {
    const items: PatientAppointmentItem[] = data?.items ?? []
    const now = new Date()
    const upcomingItems = items
      .filter((it) => toDateTime(it.date, it.time).getTime() >= now.getTime())
      .sort(
        (a, b) =>
          toDateTime(a.date, a.time).getTime() -
          toDateTime(b.date, b.time).getTime(),
      )
    const pastItems = items
      .filter((it) => toDateTime(it.date, it.time).getTime() < now.getTime())
      .sort(
        (a, b) =>
          toDateTime(b.date, b.time).getTime() -
          toDateTime(a.date, a.time).getTime(),
      )
    return {upcoming: upcomingItems, past: pastItems}
  }, [data?.items, toDateTime])

  const partner = data?.partner
  const isInvalidLink = useMemo(() => {
    if (isError) return true
    if (isLoading) return false
    const items: PatientAppointmentItem[] = data?.items ?? []
    return items.length === 0
  }, [isError, isLoading, data?.items])

  const rescheduleItem = useMemo(() => {
    if (!openRescheduleId) return null
    return (
      data?.items.find(
        (i: PatientAppointmentItem) => i.slotId === openRescheduleId,
      ) || null
    )
  }, [openRescheduleId, data?.items])
  const cancelItem = useMemo(() => {
    if (!openCancelId) return null
    return (
      data?.items.find(
        (i: PatientAppointmentItem) => i.slotId === openCancelId,
      ) || null
    )
  }, [openCancelId, data?.items])
  const headerSubtitle = useMemo(() => {
    if (!partner) return ''
    return `${rescheduleItem?.serviceName ?? ''}${
      partner.address ? ` • ${partner.address}` : ''
    }`
  }, [partner, rescheduleItem?.serviceName])
  const headerSubtitleCancel = useMemo(() => {
    if (!partner) return ''
    return `${cancelItem?.serviceName ?? ''}${
      partner.address ? ` • ${partner.address}` : ''
    }`
  }, [partner, cancelItem?.serviceName])

  const activeServiceName = useMemo(() => {
    if (openRescheduleId) return rescheduleItem?.serviceName
    if (openCancelId) return cancelItem?.serviceName
    return undefined
  }, [
    openRescheduleId,
    openCancelId,
    rescheduleItem?.serviceName,
    cancelItem?.serviceName,
  ])

  const activeHeaderSubtitle = useMemo(() => {
    if (openRescheduleId) return headerSubtitle
    if (openCancelId) return headerSubtitleCancel
    return undefined
  }, [openRescheduleId, openCancelId, headerSubtitle, headerSubtitleCancel])

  // Success modal control
  const [showSuccessModal, setShowSuccessModal] = useState(false)
  const successTitle = useMemo(() => {
    if (openRescheduleId && selectedDate && selectedTime) {
      return t('Appointment rescheduled successfully')
    }
    if (openCancelId) {
      return t('Appointment cancelled successfully')
    }
    return ''
  }, [openRescheduleId, openCancelId, selectedDate, selectedTime, t])

  const showProcessingOverlay =
    (openRescheduleId &&
      (isFetchingAvailabilities ||
        isLoadingAvailabilities ||
        rescheduleMutation.isPending)) ||
    false

  const handleSuccessModalClose = useCallback(() => {
    setShowSuccessModal(false)
    setOpenRescheduleId(null)
    setOpenCancelId(null)
    setSelectedTime(null)
    setRescheduleComment('')
    setCancelComment('')
    setApiMonthSpec('FIRST')
    setCalendarYyMm(currentYyMm())
    didSetCalendarFromApiRef.current = false
    if (isMobileContext) {
      window.location.href = 'cushla://bookings-complete'
    }
  }, [isMobileContext])

  return (
    <PageWrapper>
      <ProcessingOverlay isVisible={showProcessingOverlay} />
      <BookingsHeader
        logoUrl={partner?.logoUrl || undefined}
        partnerName={partner?.name || undefined}
        partnerServiceName={activeServiceName}
        headerSubtitle={activeHeaderSubtitle}
        confirmed={false}
      />

      {isInvalidLink && (
        <Container
          direction="column"
          justify="center"
          align="center"
          spacing={3}>
          <Typography size="xl" weight="bold">
            {t('Invalid or expired link')}
          </Typography>
          <Typography>
            {t('The link you used is invalid or has expired.')}
          </Typography>
        </Container>
      )}

      {!isInvalidLink && !openRescheduleId && !openCancelId && (
        <>
          <AppointmentsList
            title={t('Upcoming appointments')}
            items={upcoming}
            onClickHistory={setOpenSlotId}
            onClickReschedule={setOpenRescheduleId}
            onClickCancel={setOpenCancelId}
          />
          <AppointmentsList
            title={t('Past appointments')}
            items={past}
            onClickHistory={setOpenSlotId}
          />
        </>
      )}

      {!isInvalidLink && openRescheduleId && (
        <>
          {selectedDate && (
            <SelectedGlobal>
              <Typography size="xl" weight="bold">
                {selectedDateLegend}
              </Typography>
            </SelectedGlobal>
          )}

          <ReschedulePanel
            currentYyMm={effectiveYyMm}
            onMonthChange={(yyMm) => {
              setCalendarYyMm(yyMm)
              setApiMonthSpec(yyMm)
            }}
            cells={cells}
            slotsByDate={slotsByDate}
            selectedDate={selectedDate}
            selectedTime={selectedTime}
            onSelectDate={(dateKey) => {
              setSelectedDate(dateKey)
              setSelectedTime(null)
            }}
            onSelectTime={(time) => setSelectedTime(time)}
            isLoading={
              isFetchingAvailabilities ||
              isLoadingAvailabilities ||
              rescheduleMutation.isPending
            }
            timeZoneId={partner?.timeZoneId}
            comment={rescheduleComment}
            onChangeComment={setRescheduleComment}
            onBack={() => {
              setOpenRescheduleId(null)
              setSelectedTime(null)
              setRescheduleComment('')
              setApiMonthSpec('FIRST')
              setCalendarYyMm(currentYyMm())
              didSetCalendarFromApiRef.current = false
            }}
            onConfirm={() => {
              if (
                !selectedTime ||
                !selectedDate ||
                !rescheduleItem ||
                !rescheduleServiceId
              )
                return
              rescheduleMutation.mutate(
                {
                  urlParams: {
                    partnerServiceId: rescheduleServiceId,
                    fromPartnerServiceSlotId: rescheduleItem.slotId,
                  },
                  requestBody: {
                    date: selectedDate,
                    time: selectedTime,
                    comment: rescheduleComment || null,
                  },
                },
                {
                  onSuccess: () => {
                    addNotification(
                      t('Appointment rescheduled successfully'),
                      NotificationType.Success,
                    )
                    setShowSuccessModal(true)
                  },
                  onError: (error: Error) => {
                    addNotification(error.message, NotificationType.Error)
                  },
                },
              )
            }}
          />
        </>
      )}

      {!isInvalidLink && openCancelId && cancelItem && (
        <CancelPanel
          dateLegend={formatDateLegend(cancelItem?.date, cancelItem?.time)}
          comment={cancelComment}
          onChangeComment={setCancelComment}
          onBack={() => {
            setOpenCancelId(null)
            setCancelComment('')
          }}
          onConfirm={() => {
            if (!cancelItem) return
            const {partnerServiceId} = cancelItem
            cancelMutation.mutate(
              {
                urlParams: {
                  partnerServiceId,
                  partnerServiceSlotId: cancelItem.slotId,
                  comment: cancelComment || undefined,
                },
              },
              {
                onSuccess: () => {
                  addNotification(
                    t('Appointment cancelled successfully'),
                    NotificationType.Success,
                  )
                  setShowSuccessModal(true)
                },
                onError: (error: Error) => {
                  addNotification(error.message, NotificationType.Error)
                },
              },
            )
          }}
        />
      )}

      <FooterInfo
        partner={{
          address: partner?.address,
          phone: partner?.phone,
          website: partner?.website,
          email: partner?.email,
          termsAndConditionsUrl: partner?.termsAndConditionsUrl,
          privacyPolicyUrl: partner?.privacyPolicyUrl,
        }}
        termsLabel={t('Terms & Conditions')}
        privacyLabel={t('Privacy Policy')}
      />
      <PoweredBy poweredByLabel={t('Powered by')} />
      <div id="modal" />

      <SlotHistoryModal
        isVisible={!!openSlotId}
        onClose={() => setOpenSlotId(null)}
        openSlotId={openSlotId}
        items={data?.items as PatientAppointmentItem[] | undefined}
      />

      <Modal
        header=""
        footer={
          <Container justify="center">
            <OrangeButton onClick={handleSuccessModalClose}>
              {t('OK')}
            </OrangeButton>
          </Container>
        }
        isVisible={showSuccessModal}
        onClose={handleSuccessModalClose}>
        <Container direction="column" align="center" justify="center">
          <Typography textAlign="center">{successTitle}</Typography>
        </Container>
      </Modal>
    </PageWrapper>
  )
}
